import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Page-level error boundary.
 * Wrap a single page's content so a render error shows a recoverable message
 * instead of a blank screen. Not meant to wrap the whole app.
 *
 * Usage: <PageErrorBoundary pageName="HAVVA"><Content /></PageErrorBoundary>
 */
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(`[PageErrorBoundary] ${this.props.pageName || 'Page'} render error:`, error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="max-w-xl mx-auto mt-12">
        <div className="card text-center py-10" role="alert">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">Something went wrong on this page</h2>
          <p className="mt-2 text-sm text-gray-500">
            {this.props.pageName ? `The ${this.props.pageName} page` : 'This page'} hit an unexpected error. Reloading usually fixes it.
          </p>
          {this.state.error?.message && (
            <p className="mt-2 text-xs text-gray-400 break-words">Details: {this.state.error.message}</p>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 btn-primary"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
